import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { PermissionsService } from 'shared-lib';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AssetRatingService } from 'shared-lib';
import { AssetRating } from '../../../models/asset-rating.model';
import { PermissionsEnum } from 'shared-lib';
import { Observable } from 'rxjs';
import { BPSAdminLevel } from 'shared-lib';
import { TranslationUtils } from '../../../../assets/i18n/translation.utils';
import { UtilitySharedService } from '../../../shared/_services/utility-shared.service';

@Component({
  selector: 'app-crud-asset-rating',
  templateUrl: './crud-asset-rating.component.html',
  styleUrl: './crud-asset-rating.component.scss',
  standalone: false,
})
export class CrudAssetRatingComponent implements OnInit {
  public PermissionsEnum = PermissionsEnum;
  assetRatings: AssetRating[] = [];
  public currentPage: number = 1;
  public pageSize: number = 10;
  public count: number = 0;
  selectedAssetRating: AssetRating = new AssetRating();
  updatedAssetRating: AssetRating = new AssetRating();
  updatedRating: number | null = null;
  updatedUnit: string = '';
  isEditMode: boolean = false;
  searchText: string = '';
  utilityId: number = 0;
  actionLabel$: Observable<string>;
  messages$: Observable<{ [key: string]: string }>;

  @Input() bpsAdminLevel!: BPSAdminLevel;

  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }

  @ViewChild('closeEditModal') closeEditModal: ElementRef;

  constructor(
    private assetRatingService: AssetRatingService,
    public permissionsService: PermissionsService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private utilitySharedService: UtilitySharedService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);
    if (this.utilityId !== 0) {
      this.reloadTable();
    }
    this.updateActionLabel();
  }

  private updateActionLabel(): void {
    const key = this.isEditMode ? TranslationUtils.EDIT_KEY : TranslationUtils.NEW_KEY;
    this.actionLabel$ = this.translateService.stream(key);
  }

  openAddModal() {
    this.isEditMode = false;
    this.selectedAssetRating = new AssetRating();
    this.updatedAssetRating = new AssetRating();
    this.updatedRating = null;
    this.updatedUnit = '';
    this.updateActionLabel();
  }

  openEditModal() {
    this.isEditMode = true;
    this.updatedAssetRating = { ...this.selectedAssetRating };
    this.setUpdatedRatingAndUnit();
    this.updateActionLabel();
  }

  selectAssetRating(assetRating: AssetRating) {
    this.selectedAssetRating = { ...assetRating };
    this.openEditModal();
  }

  onDeleteAssetRating(assetRating: AssetRating){
    this.selectedAssetRating = { ...assetRating };
    this.isEditMode = false;
  }

  onSearch() {
    this.currentPage = 1;
    this.reloadTable(1);
  }

  onSearchChange() {
    if (this.searchText === '' || this.searchText.trim() === '') {
      this.onSearch();
    }
  }

  reloadTable(page: number | null = null) {
    if (page) {
      this.currentPage = page;
    }
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        SearchValue: this.searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllAssetRatings(dataIn);
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.reloadTable(newPage);
  }

  private getAllAssetRatings(dataIn: any) {
    this.assetRatingService.getAll(dataIn).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.assetRatings = response.data.data;
          this.count = response.data.count;
          if (this.count === 0) {
            this.toastr.info('No data for given input.');
          }
        } else {
          this.toastr.error(response?.message || 'Unexpected response from server.');
          this.assetRatings = [];
          this.count = 0;
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
          this.toastr.error(messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]);
        });
        this.assetRatings = [];
        this.count = 0;
      },
    });
  }

  save() {
    if (!this.updatedRating || this.updatedRating < 0) {
      this.toastr.warning("Please enter a rating value.");
      return;
    }
    if (!this.updatedUnit || this.updatedUnit === '') {
      this.toastr.warning("Please select a rating unit.");
      return;
    }
    this.normalizeAssetRatingValue();
    this.updatedAssetRating.utilityId = this.utilityId;
    this.updatedAssetRating.id = this.isEditMode ? this.selectedAssetRating.id : 0;

    this.assetRatingService.save(this.updatedAssetRating).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success(this.isEditMode ? "Asset Rating successfully updated." : "Asset Rating successfully added.");
          this.closeEditModal?.nativeElement?.click();
          setTimeout(() => {
            this.reloadTable(1);
          }, 500);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: (_) => {
        this.messages$?.subscribe((messages) => {
          this.toastr.error(messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]);
        });
      },
    });
  }

  deleteAssetRating() {
    this.assetRatingService.delete(this.selectedAssetRating.id).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success(response?.message);
          this.onPageChange(1);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  private normalizeAssetRatingValue() {
    this.updatedAssetRating.kvaRating = this.updatedRating + ' ' + this.updatedUnit;
  }

  private setUpdatedRatingAndUnit() {
    const rawRatingWithUnit = this.updatedAssetRating?.kvaRating ?? '';
    const [ratingString, unitString] = rawRatingWithUnit.split(' ');
    const rating = Number(ratingString);

    this.updatedRating = isNaN(rating) ? 0 : rating;
    const unitLower = unitString?.toLowerCase();
    if (unitLower === 'kva') {
      this.updatedUnit = 'kVA';
    } else if (unitLower === 'mva') {
      this.updatedUnit = 'MVA';
    } else {
      this.updatedUnit = '';
    }
  }
}